import React, { useState, useMemo, useEffect } from 'react';
import { CalendarCheck, TrendingUp, TrendingDown, AlertTriangle, Users, Bell, Printer, Download } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { api } from '../../utils/api';
import StatCard from '../../components/common/StatCard';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

// A leave/excused day is admin-sanctioned absence, not truancy — it is
// tracked and shown everywhere, but by decision (Sep 2026) it is excluded
// from both sides of the attendance % so an approved leave never drags a
// student's percentage down the way an unexcused absence does.
const STATUS_COLORS = { present: '#16a34a', absent: '#dc2626', late: '#f59e0b', leave: '#6366f1', excused: '#06b6d4' };
const STATUS_LABELS = { present: 'Present', absent: 'Absent', late: 'Late', leave: 'Leave', excused: 'Excused' };
const STATUS_SHORT  = { present: 'P', absent: 'A', late: 'L', leave: 'Lv', excused: 'Ex' };

function tallyStatus(records) {
  const c = { present: 0, absent: 0, late: 0, leave: 0, excused: 0 };
  records.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
  return c;
}
// Late still counts toward the numerator (a late arrival is still attendance)
// — only Absent, and the excluded Leave/Excused days, don't.
function pctFromCounts(c) {
  const workingTotal = c.present + c.absent + c.late;
  return workingTotal > 0 ? Math.round(((c.present + c.late) / workingTotal) * 100) : null;
}

function getWeekRange(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { start: mon.toISOString().split('T')[0], end: sun.toISOString().split('T')[0] };
}

function getPeriodRange(period, dateStr) {
  if (period === 'day') return { start: dateStr, end: dateStr };
  if (period === 'week') return getWeekRange(dateStr);
  const d = new Date(dateStr + 'T00:00:00');
  const y = d.getFullYear(), m = d.getMonth();
  const lastDay = new Date(y, m + 1, 0).getDate();
  return { start: `${y}-${String(m + 1).padStart(2, '0')}-01`, end: `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
}

function shiftDate(dateStr, deltaDays) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().split('T')[0];
}

function getPrevPeriodAnchor(period, dateStr) {
  if (period === 'day') return shiftDate(dateStr, -1);
  if (period === 'week') return shiftDate(dateStr, -7);
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function formatDisplay(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

// GitHub-style month grid — replaces the flat row of day chips when viewing
// a full month, so a whole month's pattern reads at a glance instead of
// requiring horizontal scanning of 30 chips in a row.
function MonthHeatmap({ anchorDate, records }) {
  const d = new Date(anchorDate + 'T00:00:00');
  const year = d.getFullYear(), month = d.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDay = {};
  records.forEach(r => { byDay[Number(r.date.slice(8, 10))] = r.status; });
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  return (
    <div className="max-w-md">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['S','M','T','W','T','F','S'].map((lbl, i) => (
          <span key={i} className="text-center text-gray-400 font-medium">{lbl}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) return <div key={i} />;
          const status = byDay[day];
          const color = status ? STATUS_COLORS[status] : null;
          return (
            <div key={i} title={status ? `${day} — ${STATUS_LABELS[status]}` : `${day} — no record`}
              className="aspect-square rounded flex items-center justify-center font-semibold"
              style={color ? { background: `${color}22`, color, border: `1px solid ${color}66` } : { background: '#f9fafb', color: '#d1d5db', border: '1px solid #f3f4f6' }}>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AttendanceReports() {
  const { attendance, students, classes, refreshAttendance } = useApp();
  const { currentUser } = useAuth();
  const toast = useToast();
  const isTeacher = currentUser?.role === 'teacher';
  const availableClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;

  // The web app has no live sync — without this, attendance a teacher marks
  // from the mobile app never shows up here until the admin manually
  // reloads the page. Poll while this report is open instead (same pattern
  // as AdminDashboard).
  useEffect(() => {
    const id = setInterval(refreshAttendance, 30000);
    return () => clearInterval(id);
  }, [refreshAttendance]);

  const [period, setPeriod] = useState('month'); // day | week | month
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [threshold, setThreshold] = useState(75);
  const [selectedDefaulters, setSelectedDefaulters] = useState(new Set());
  const [notifying, setNotifying] = useState(false);

  // Years available in the dropdown — always includes the currently
  // selected year even if it's outside the default nearby range, so
  // switching period never leaves selectedDate's year unreachable.
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const selected = Number(selectedDate.slice(0, 4));
    const years = new Set([selected, current - 1, current, current + 1]);
    return [...years].sort((a, b) => a - b);
  }, [selectedDate]);

  const periodLabel = useMemo(() => {
    if (period === 'day') return formatDisplay(selectedDate);
    if (period === 'week') {
      const { start, end } = getWeekRange(selectedDate);
      return `${formatDisplay(start)} – ${formatDisplay(end)}`;
    }
    return new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [period, selectedDate]);

  const { start: rangeStart, end: rangeEnd } = useMemo(() => getPeriodRange(period, selectedDate), [period, selectedDate]);

  const filteredAttendance = useMemo(() =>
    attendance.filter(a => a.entityType === 'student' && a.date >= rangeStart && a.date <= rangeEnd),
    [attendance, rangeStart, rangeEnd]);

  // Every active student this admin/teacher can see, independent of the
  // class dropdown — used for the cross-class comparison chart so picking
  // one class in the filter doesn't hide the comparison itself.
  const scopedStudents = useMemo(() =>
    students.filter(s => s.status === 'active' && availableClasses.some(c => c.id === s.classId)),
    [students, availableClasses]);

  const visibleStudents = useMemo(() => {
    return scopedStudents
      .filter(s => selectedClass === 'all' || s.classId === selectedClass)
      .sort((a, b) => {
        if (a.classId !== b.classId) return a.classId.localeCompare(b.classId);
        return Number(a.rollNumber) - Number(b.rollNumber);
      });
  }, [scopedStudents, selectedClass]);

  const visibleStudentIds = useMemo(() => new Set(visibleStudents.map(s => s.id)), [visibleStudents]);

  const studentRows = useMemo(() => {
    return visibleStudents.map(s => {
      const records = filteredAttendance.filter(a => a.entityId === s.id);
      const counts = tallyStatus(records);
      const pct = pctFromCounts(counts);
      const cls = classes.find(c => c.id === s.classId);
      return { ...s, ...counts, totalMarked: records.length, pct, className: cls ? `${cls.name}-${cls.section}` : '-', records };
    });
  }, [visibleStudents, filteredAttendance, classes]);

  const datesInView = useMemo(() => {
    const set = new Set(filteredAttendance.map(a => a.date));
    return [...set].sort();
  }, [filteredAttendance]);

  // ── KPIs ──────────────────────────────────────────────────────
  const viewCounts = useMemo(() =>
    tallyStatus(filteredAttendance.filter(a => visibleStudentIds.has(a.entityId))),
    [filteredAttendance, visibleStudentIds]);
  const overallPct = pctFromCounts(viewCounts);

  const prevAnchor = useMemo(() => getPrevPeriodAnchor(period, selectedDate), [period, selectedDate]);
  const prevRange = useMemo(() => getPeriodRange(period, prevAnchor), [period, prevAnchor]);
  const prevPct = useMemo(() => {
    const recs = attendance.filter(a => a.entityType === 'student' && a.date >= prevRange.start && a.date <= prevRange.end && visibleStudentIds.has(a.entityId));
    return pctFromCounts(tallyStatus(recs));
  }, [attendance, prevRange, visibleStudentIds]);
  const trendDelta = (overallPct !== null && prevPct !== null) ? overallPct - prevPct : null;

  const todayStr = new Date().toISOString().split('T')[0];
  const todayCounts = useMemo(() =>
    tallyStatus(attendance.filter(a => a.entityType === 'student' && a.date === todayStr && visibleStudentIds.has(a.entityId))),
    [attendance, todayStr, visibleStudentIds]);
  const todayPct = pctFromCounts(todayCounts);
  const todayMarked = Object.values(todayCounts).some(v => v > 0);

  const defaulters = useMemo(() =>
    studentRows.filter(s => s.pct !== null && s.pct < threshold).sort((a, b) => a.pct - b.pct),
    [studentRows, threshold]);

  // ── Charts ────────────────────────────────────────────────────
  const statusChartData = useMemo(() =>
    ['present','absent','late','leave','excused']
      .map(k => ({ key: k, name: STATUS_LABELS[k], value: viewCounts[k], color: STATUS_COLORS[k] }))
      .filter(d => d.value > 0),
    [viewCounts]);

  const trendChartData = useMemo(() => {
    if (period === 'day') return [];
    const byDate = {};
    filteredAttendance.filter(a => visibleStudentIds.has(a.entityId)).forEach(a => {
      (byDate[a.date] = byDate[a.date] || []).push(a);
    });
    return Object.keys(byDate).sort().map(date => ({
      date: formatDisplay(date).slice(0, 5),
      pct: pctFromCounts(tallyStatus(byDate[date])) ?? 0,
    }));
  }, [filteredAttendance, visibleStudentIds, period]);

  const classComparisonData = useMemo(() => {
    const byClass = {};
    filteredAttendance.forEach(a => {
      const stu = students.find(s => s.id === a.entityId);
      const cid = a.classId || stu?.classId;
      if (!cid || !availableClasses.some(c => c.id === cid)) return;
      (byClass[cid] = byClass[cid] || []).push(a);
    });
    return availableClasses
      .map(c => ({ classId: c.id, name: `${c.name}-${c.section}`, pct: pctFromCounts(tallyStatus(byClass[c.id] || [])), hasData: (byClass[c.id] || []).length > 0 }))
      .filter(r => r.hasData && r.pct !== null)
      .sort((a, b) => a.pct - b.pct);
  }, [filteredAttendance, students, availableClasses]);

  // ── Actions ───────────────────────────────────────────────────
  const toggleDefaulter = (id) => setSelectedDefaulters(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const notifySelected = async () => {
    const picked = defaulters.filter(d => selectedDefaulters.has(d.id));
    if (!picked.length) return;
    // This sends a real push + WhatsApp message to real parents — same
    // caution as the fee-due-reminder sends elsewhere in the app.
    const names = picked.map(d => `${d.firstName} ${d.lastName}`).join(', ');
    if (!window.confirm(`Send a low-attendance alert (push + WhatsApp) to the parents of ${picked.length} student(s)?\n\n${names}`)) return;
    const targets = picked.map(d => ({ studentId: d.id, pct: d.pct }));
    setNotifying(true);
    try {
      const res = await api.notifyLowAttendance(targets, periodLabel);
      toast?.success(`Notified ${res?.sent ?? targets.length} parent(s)`);
      setSelectedDefaulters(new Set());
    } catch (e) {
      toast?.error(`Notify failed: ${e.message}`);
    }
    setNotifying(false);
  };

  const exportCsv = () => {
    const header = ['Student','Class','Present','Absent','Late','Leave','Excused','Attendance %'];
    const rows = studentRows.map(s => [
      `${s.firstName} ${s.lastName}`, s.className, s.present, s.absent, s.late, s.leave, s.excused,
      s.pct === null ? '' : s.pct,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance-${period}-${selectedDate}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printRegister = async () => {
    const school = await getSchoolInfo();
    const rows = studentRows.map((s, i) => `<tr>
      <td class="center">${i + 1}</td><td>${s.firstName} ${s.lastName}</td><td>${s.className}</td>
      <td class="center">${s.present}</td><td class="center">${s.absent}</td><td class="center">${s.late}</td>
      <td class="center">${s.leave}</td><td class="center">${s.excused}</td>
      <td class="center"><strong>${s.pct === null ? '—' : s.pct + '%'}</strong></td>
    </tr>`).join('');
    const win = window.open('', '_blank', 'width=1000,height=700');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Attendance Register</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; padding:24px 28px; color:#111; font-size:12px; }
  .meta { display:flex; justify-content:space-between; margin-bottom:12px; }
  .meta .title { font-size:15px; font-weight:700; color:#4f46e5; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 8px; text-align:left; border-bottom:2px solid #d1d5db; white-space:nowrap; }
  thead th.center { text-align:center; }
  td { padding:6px 8px; border-bottom:1px solid #f3f4f6; }
  td.center { text-align:center; }
  tr:nth-child(even) { background:#fafafa; }
  .footer { text-align:center; font-size:11px; color:#aaa; margin-top:20px; padding-top:8px; border-top:1px dashed #e5e7eb; }
  .print-btn { text-align:center; margin-top:14px; }
  @page { margin: 3mm; } @media print { .print-btn{display:none;} body{padding:4px;} * { color:#000 !important; } }
</style></head><body>
<div class="school-header">${schoolHeader(school, '#4f46e5', `Attendance Register — ${periodLabel}`)}</div>
<div class="meta">
  <div><div class="title">${selectedClass === 'all' ? 'All Classes' : (classes.find(c => c.id === selectedClass) ? `${classes.find(c => c.id === selectedClass).name}-${classes.find(c => c.id === selectedClass).section}` : '')}</div><div style="color:#555;margin-top:2px">${studentRows.length} students</div></div>
  <div style="text-align:right;color:#555">Overall: <strong>${overallPct === null ? '—' : overallPct + '%'}</strong></div>
</div>
<table>
  <thead><tr><th class="center">#</th><th>Student</th><th>Class</th><th class="center">P</th><th class="center">A</th><th class="center">L</th><th class="center">Lv</th><th class="center">Ex</th><th class="center">%</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Computer-generated — ${school.name}</div>
<div class="print-btn"><button onclick="window.print()" style="background:#4f46e5;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',sans-serif">🖨 Print</button></div>
</body></html>`); win.document.close();
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Attendance Reports</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {['day', 'week', 'month'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-4 py-1 text-sm font-medium capitalize ${period === p ? 'bg-indigo-600 text-white' : 'bg-white hover:bg-gray-50'}`}>
                {p}
              </button>
            ))}
          </div>
          {period === 'month' ? (
            <div className="flex items-center gap-1.5">
              <select
                value={Number(selectedDate.slice(5, 7)) - 1}
                onChange={e => {
                  const month = String(Number(e.target.value) + 1).padStart(2, '0');
                  setSelectedDate(`${selectedDate.slice(0, 4)}-${month}-01`);
                }}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {MONTH_NAMES.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select
                value={selectedDate.slice(0, 4)}
                onChange={e => setSelectedDate(`${e.target.value}-${selectedDate.slice(5, 7)}-01`)}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          ) : (
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          )}
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            <Download size={14}/> CSV
          </button>
          <button onClick={printRegister} className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            <Printer size={14}/> Print
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
          className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="all">All Classes</option>
          {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
        </select>
        <span className="text-sm text-gray-500">{periodLabel}</span>
        <span className="text-sm text-gray-300">|</span>
        <label className="flex items-center gap-1.5 text-sm text-gray-500">
          Alert below
          <input type="number" min="1" max="100" value={threshold}
            onChange={e => setThreshold(Math.min(100, Math.max(1, Number(e.target.value) || 75)))}
            className="w-14 px-2 py-0.5 border border-gray-200 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          %
        </label>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Today's Attendance"
          value={todayMarked ? `${todayPct}%` : '—'}
          subtext={todayMarked ? `${todayCounts.present + todayCounts.late}/${todayCounts.present + todayCounts.absent + todayCounts.late} marked present` : 'Not marked yet'}
          colorClass={!todayMarked ? 'bg-gray-400' : todayPct >= 75 ? 'bg-green-500' : 'bg-red-500'} />
        <StatCard icon={trendDelta === null ? TrendingUp : trendDelta >= 0 ? TrendingUp : TrendingDown}
          label={`${period === 'day' ? "Day's" : period === 'week' ? "Week's" : "Month's"} Average`}
          value={overallPct === null ? '—' : `${overallPct}%`}
          subtext={trendDelta === null ? 'No prior-period data' : `${trendDelta >= 0 ? '▲' : '▼'} ${Math.abs(trendDelta)} pts vs last ${period}`}
          colorClass={overallPct === null ? 'bg-gray-400' : overallPct >= 75 ? 'bg-green-500' : 'bg-orange-500'} />
        <StatCard icon={AlertTriangle} label={`Below ${threshold}%`}
          value={defaulters.length}
          subtext="students need attention"
          colorClass={defaulters.length > 0 ? 'bg-red-500' : 'bg-green-500'} />
        <StatCard icon={Users} label="Students in View"
          value={visibleStudents.length}
          subtext={selectedClass === 'all' ? 'All classes' : (classes.find(c => c.id === selectedClass)?.name || '')}
          colorClass="bg-indigo-500" />
      </div>

      {/* Status breakdown + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:col-span-1">
          <h3 className="font-semibold mb-3">Status Breakdown — {periodLabel}</h3>
          {statusChartData.length === 0 ? (
            <p className="text-gray-400 text-sm py-10 text-center">No attendance marked for this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusChartData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {statusChartData.map(d => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} record(s)`, n]} />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
          <h3 className="font-semibold mb-3">Daily Trend</h3>
          {period === 'day' ? (
            <p className="text-gray-400 text-sm py-16 text-center">Switch to Week or Month to see a trend.</p>
          ) : trendChartData.length === 0 ? (
            <p className="text-gray-400 text-sm py-16 text-center">No attendance marked for this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendChartData} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => `${v}%`} />
                <ReferenceLine y={threshold} stroke="#9ca3af" strokeDasharray="4 4" label={{ value: `${threshold}%`, fontSize: 10, fill: '#9ca3af', position: 'insideTopLeft' }} />
                <Line type="monotone" dataKey="pct" name="Attendance %" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Class comparison */}
      {classComparisonData.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Class Comparison — {periodLabel}</h3>
          <ResponsiveContainer width="100%" height={Math.max(160, classComparisonData.length * 34)}>
            <BarChart data={classComparisonData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
              <Tooltip formatter={v => `${v}%`} />
              <ReferenceLine x={threshold} stroke="#9ca3af" strokeDasharray="4 4" />
              <Bar dataKey="pct" name="Attendance %" radius={[0, 4, 4, 0]}>
                {classComparisonData.map(d => <Cell key={d.classId} fill={d.pct < threshold ? '#dc2626' : '#4f46e5'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Defaulters panel */}
      {defaulters.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-red-50 border-b border-red-100">
            <h3 className="font-semibold text-red-700 flex items-center gap-1.5"><AlertTriangle size={16}/> Defaulters — below {threshold}% ({defaulters.length})</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelectedDefaulters(new Set(defaulters.map(d => d.id)))} className="text-xs font-medium text-indigo-600 hover:underline">Select All</button>
              {selectedDefaulters.size > 0 && (
                <button onClick={notifySelected} disabled={notifying}
                  className="flex items-center gap-1.5 px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                  <Bell size={13}/> {notifying ? 'Sending…' : `Notify Parents (${selectedDefaulters.size})`}
                </button>
              )}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {defaulters.map(s => (
                  <tr key={s.id} className="border-b border-gray-50 last:border-0 hover:bg-red-50/30">
                    <td className="px-4 py-1.5 w-8">
                      <input type="checkbox" checked={selectedDefaulters.has(s.id)} onChange={() => toggleDefaulter(s.id)} />
                    </td>
                    <td className="px-2 py-1.5 font-medium">{s.firstName} {s.lastName}</td>
                    <td className="px-2 py-1.5 text-gray-500">{s.className}</td>
                    <td className="px-2 py-1.5 text-gray-400">{s.present}P / {s.absent}A / {s.late}L</td>
                    <td className="px-4 py-1.5 text-right font-bold text-red-600 w-16">{s.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-4 flex-wrap">
        {Object.keys(STATUS_LABELS).map(k => (
          <span key={k} className="flex items-center gap-1">
            <span className="text-white rounded px-1.5 py-0.5 font-bold" style={{ background: STATUS_COLORS[k] }}>{STATUS_SHORT[k]}</span>
            <span className="text-gray-500">{STATUS_LABELS[k]}</span>
          </span>
        ))}
        <span className="text-gray-400 italic">Leave &amp; Excused don't count against attendance %</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="px-4 py-1.5 text-left font-bold">Student</th>
              <th className="px-4 py-1.5 text-left font-bold">Class</th>
              <th className="px-4 py-1.5 text-center font-bold">P</th>
              <th className="px-4 py-1.5 text-center font-bold">A</th>
              <th className="px-4 py-1.5 text-center font-bold">L</th>
              <th className="px-4 py-1.5 text-center font-bold">Lv</th>
              <th className="px-4 py-1.5 text-center font-bold">Ex</th>
              <th className="px-4 py-1.5 text-center font-bold w-40">Attendance %</th>
            </tr>
          </thead>
          <tbody>
            {studentRows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center">No attendance records for this period.</td></tr>
            )}
            {studentRows.map(s => (
              <React.Fragment key={s.id}>
                <tr
                  className="border-b border-gray-100 hover:bg-indigo-50/30 cursor-pointer"
                  onClick={() => setExpandedStudent(expandedStudent === s.id ? null : s.id)}
                >
                  <td className="px-4 py-1.5 font-medium">
                    <span className="flex items-center gap-1.5">
                      <span className={`transition-transform ${expandedStudent === s.id ? 'rotate-90' : ''}`}>▶</span>
                      {s.firstName} {s.lastName}
                    </span>
                  </td>
                  <td className="px-4 py-1.5">{s.className}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-green-600">{s.present}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-red-500">{s.absent}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-yellow-600">{s.late}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-indigo-500">{s.leave}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-cyan-600">{s.excused}</td>
                  <td className="px-4 py-1.5">
                    {s.pct === null ? (
                      <span className="text-gray-400 text-center block">—</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${s.pct >= 75 ? 'bg-green-500' : s.pct >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${s.pct}%` }} />
                        </div>
                        <span className={`font-semibold w-9 text-right ${s.pct >= 75 ? 'text-green-600' : s.pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {s.pct}%
                        </span>
                      </div>
                    )}
                  </td>
                </tr>

                {/* Day-wise detail */}
                {expandedStudent === s.id && (
                  <tr className="bg-indigo-50/20 border-b border-gray-100">
                    <td colSpan={8} className="px-6 py-3">
                      {s.records.length === 0 ? (
                        <p className="text-sm">No records marked for this student in this period.</p>
                      ) : period === 'month' ? (
                        <MonthHeatmap anchorDate={selectedDate} records={s.records} />
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {datesInView.map(date => {
                            const rec = s.records.find(r => r.date === date);
                            const st = rec?.status || null;
                            const color = st ? STATUS_COLORS[st] : null;
                            return (
                              <div key={date} className="flex flex-col items-center gap-0.5">
                                <span className="text-gray-400">
                                  {(() => { const d = new Date(date + 'T00:00:00'); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`; })()}
                                </span>
                                <span className="font-bold rounded px-2 py-0.5"
                                  style={color ? { background: color, color: '#fff' } : { background: '#f3f4f6', color: '#9ca3af' }}>
                                  {st ? STATUS_SHORT[st] : '—'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AttendanceReports;
